import {atom, type Getter, type Setter} from 'jotai';
import {getProducts} from '@/actions/products';
import type {Product, SearchState, VisibleColumns} from '@/types/product';
import type {Category} from '@/types/categories';

const initialState: SearchState = {
    page: 0,
    total: 0,
    items: [],
    query: '',
    skus: null,
    error: null,
    tagId: null,
    smart: null,
    per_page: 40,
    dateTo: null,
    loading: false,
    category: null,
    dateFrom: null,
    supplierId: null,
    subcategory: null,
    sortOrder: 'desc',
    initialized: false,
    status: 'approved',
    productviewer: null,
    sortBy: 'created_at',
};

export const productsAtom = atom<SearchState>(initialState);
export const selectedProductsAtom = atom<Product[]>([]);
export const activeProductAtom = atom<Product | null>(null);
export const selectedProductAtom = atom<Product | null>(null);
export const visibleColumnsAtom = atom<VisibleColumns>({
    sku: true,
    id: false,
    url: true,
    name: true,
    image: true,
    status: true,
    brand: false,
    price: false,
    width: false,
    depth: false,
    color: false,
    height: false,
    category: true,
    currency: false,
    created_at: false,
    completed_at: false,
    category_path: false,
    supplier_name: false,
});

export const productsSearchParamsAtom = atom({
    page: 0,
    query: '',
    skus: null,
    tagId: null,
    smart: null,
    per_page: 40,
    category: null,
    supplierId: null,
    subcategory: null,
    sortOrder: 'desc',
    status: 'approved',
    productviewer: null,
    sortBy: 'created_at',
});

// Date filter atoms
export const dateFilterFromAtom = atom<string>('');
export const dateFilterToAtom = atom<string>('');

// Max items to display in the list view (for performance)
export const maxListItemsAtom = atom<number>(50);

// List view profiles for saving/loading column and filter configurations
export interface ListViewProfile {
    id: string;
    name: string;
    createdAt: string;
    visibleColumns: string[];
    filters: Array<{
        column: string;
        operator: 'includes' | 'excludes';
        value: string;
    }>;
}

export const listViewProfilesAtom = atom<ListViewProfile[]>([]);

// Material variations selected in the sidebar material picker
// Maps material name to variation id
export const selectedMaterialVariationsAtom = atom<Record<string, number>>({});

// Array to store a selected category path (supports unlimited nesting levels)
export const selectedCategoryPathAtom = atom<Category[]>([]);

export const getProductsAtom = atom(null, (get, set) =>
    fetchProducts(get, set)
);

async function fetchProducts(get: Getter, set: Setter) {
    const productsState = get(productsAtom);

    set(productsAtom, (prev: SearchState) => ({
        ...prev,
        error: null,
        loading: true,
    }));

    try {
        const {items, total} = await getProducts({
            page: productsState.page,
            sortBy: productsState.sortBy,
            sortOrder: productsState.sortOrder,
            skus: productsState.skus ?? undefined,
            per_page: productsState.per_page ?? 50,
            query: productsState.query ?? undefined,
            tagId: productsState.tagId ?? undefined,
            smart: productsState.smart ?? undefined,
            status: productsState.status ?? undefined,
            dateTo: productsState.dateTo ?? undefined,
            dateFrom: productsState.dateFrom ?? undefined,
            productviewer: productsState.productviewer ?? undefined,
        });

        set(productsAtom, (prev: SearchState) => {
            const newItems = items as Product[];
            if (productsState.page === 0) {
                return {
                    ...prev,
                    total,
                    error: null,
                    loading: false,
                    initialized: true,
                    items: newItems,
                };
            }
            // Deduplicate when appending - prevents duplicate keys from race conditions
            const existingIds = new Set(prev.items?.map((p) => p.id) ?? []);
            const uniqueNewItems = newItems.filter(
                (p) => !existingIds.has(p.id)
            );
            return {
                ...prev,
                total,
                error: null,
                loading: false,
                initialized: true,
                items: [...(prev.items ?? []), ...uniqueNewItems],
            };
        });
    } catch (error) {
        set(productsAtom, (prev: SearchState) => ({
            ...prev,
            loading: false,
            error:
                error instanceof Error
                    ? error.message
                    : 'Failed to fetch products',
        }));
    }
}
